// src/components/layout/GlobalSearch.tsx
// Global Search (Phase 4) — searches tasks (by title/notes/remarks keyword,
// exact id, date, or status) and the officer/employee directory (by
// name/department/email), scoped to the same assignee/assigner visibility
// every other task view already enforces (see searchController.ts).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { RootState } from 'store';
import { setSearchQuery, setFilter } from 'store/slices/uiSlice';
import { searchService, SearchResult, MatchedUser } from 'services/searchService';
import { PRIORITY } from 'utils/constants';
import { Task } from 'types/task';
import { FileMovement } from 'types/fileMovement';

export default function GlobalSearch() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { searchQuery } = useSelector((s: RootState) => s.ui);
  const [debounced, setDebounced] = useState('');
  const [focused, setFocused] = useState(false);
  // Icon-triggered search — collapses back to just the icon once blurred with
  // nothing typed, so it doesn't sit open in the header taking up space.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data, isFetching } = useQuery<SearchResult>({
    queryKey: ['search', debounced],
    queryFn: () => searchService.search(debounced),
    enabled: debounced.length > 1,
  });

  const tasks: Task[] = data?.tasks ?? [];
  const matchedUsers: MatchedUser[] = data?.matchedUsers ?? [];
  const matchedFiles: FileMovement[] = data?.matchedFiles ?? [];
  const showPanel = focused && debounced.length > 1;

  function selectTask(taskId: string) {
    navigate(`/task/${taskId}`);
    setFocused(false);
  }

  function selectUser(userId: string) {
    dispatch(setFilter({ key: 'filterAssign', value: userId }));
    navigate('/tasks/all');
    setFocused(false);
  }

  function selectFile() {
    navigate('/filemovement');
    setFocused(false);
  }

  return (
    <div className="relative">
      {expanded || searchQuery ? (
        <input
          autoFocus
          value={searchQuery}
          onChange={e => dispatch(setSearchQuery(e.target.value))}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => {
            setFocused(false);
            if (!searchQuery.trim()) setExpanded(false);
          }, 150)}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              dispatch(setSearchQuery(''));
              setExpanded(false);
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder="Search tasks, files, officers, department..."
          className="w-40 sm:w-56 md:w-72 bg-[var(--om-card)] border border-[var(--om-border)] rounded-full text-[var(--om-text)] pl-9 pr-3 py-1.5 text-xs outline-none focus:border-blue-500 placeholder:text-[var(--om-text-dim)] transition-all focus:w-72"
        />
      ) : (
        <button
          onClick={() => setExpanded(true)}
          title="Search"
          className="w-9 h-9 flex items-center justify-center rounded-full border border-[var(--om-border)] bg-[var(--om-card)] text-[var(--om-text-faint)] hover:text-blue-400 hover:border-blue-500/30 transition-all"
        >
          <Search className="w-4 h-4" strokeWidth={2} />
        </button>
      )}
      {(expanded || searchQuery) && (
        <Search className="w-3.5 h-3.5 text-[var(--om-text-dim)] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={2} />
      )}

      {showPanel && (
        <div className="absolute top-full right-0 mt-1 max-h-96 overflow-y-auto bg-[var(--om-panel)] border border-[var(--om-border)] rounded-xl shadow-2xl z-50 w-72">
          {isFetching ? (
            <p className="text-[11px] text-[var(--om-text-dim)] text-center py-4">Searching...</p>
          ) : tasks.length === 0 && matchedUsers.length === 0 && matchedFiles.length === 0 ? (
            <p className="text-[11px] text-[var(--om-text-dim)] text-center py-4">No results</p>
          ) : (
            <>
              {matchedFiles.length > 0 && (
                <div className="p-2 border-b border-[var(--om-border)]">
                  <span className="text-[10px] text-[var(--om-text-dim)] font-black uppercase tracking-widest block mb-1 px-1">
                    Files
                  </span>
                  {matchedFiles.map(f => (
                    <button key={f._id} onMouseDown={e => e.preventDefault()} onClick={selectFile}
                      className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-[var(--om-card)] transition-colors flex items-center justify-between gap-2">
                      <span className="text-[12px] text-[var(--om-text)] truncate">{f.subject}</span>
                      <span className="text-[11px] font-mono text-blue-400 flex-shrink-0">{f.fileNumber}</span>
                    </button>
                  ))}
                </div>
              )}
              {matchedUsers.length > 0 && (
                <div className="p-2 border-b border-[var(--om-border)]">
                  <span className="text-[10px] text-[var(--om-text-dim)] font-black uppercase tracking-widest block mb-1 px-1">
                    Officers / Employees
                  </span>
                  {matchedUsers.map(u => (
                    <button key={u._id} onMouseDown={e => e.preventDefault()} onClick={() => selectUser(u._id)}
                      className="w-full text-left px-2 py-1.5 rounded-lg text-xs hover:bg-[var(--om-card)] transition-colors">
                      <span className="text-[var(--om-text)] font-semibold">{u.name}</span>
                      <span className="text-[var(--om-text-dim)] ml-1.5">{u.department || u.role}</span>
                    </button>
                  ))}
                </div>
              )}
              {tasks.length > 0 && (
                <div className="p-2">
                  <span className="text-[10px] text-[var(--om-text-dim)] font-black uppercase tracking-widest block mb-1 px-1">
                    Tasks
                  </span>
                  {tasks.map(t => (
                    <button key={t._id} onMouseDown={e => e.preventDefault()} onClick={() => selectTask(t._id)}
                      className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-[var(--om-card)] transition-colors flex items-center justify-between gap-2">
                      <span className="text-[12px] text-[var(--om-text)] truncate">{t.title}</span>
                      <span className="text-[10px] font-bold flex-shrink-0" style={{ color: PRIORITY[t.priority].c }}>
                        {t.status}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
